/**
 * Who is annotating — a **read-only view of the signed-in account**.
 *
 * What changed, and why it had to
 * ------------------------------
 * This used to be a localStorage identity label with a dialog for setting it. That was a
 * deliberate v1 scope decision for a local single-team tool, and it stopped being defensible
 * the moment the service got a public hostname: `annotator_id` travelled in every write body,
 * so a box's author was whatever the browser said it was, and `data-integrity.md` §4
 * ('provenance on every annotation') was a comment rather than a property.
 *
 * The identity now comes from the bearer token. The server derives the annotator id from the
 * authenticated subject, and no request model has a field for it. So there is nothing here to
 * *set*: `id` and `name` are derived from `auth.user`, and the dialog is gone.
 *
 * Why the module still exists at all
 * ----------------------------------
 * `annotator.id`, `.name` and `.ready` are read by around fifteen components, and every one
 * of those reads is still the right question — 'who will this box be attributed to?'. Keeping
 * the façade means the change is in one place instead of fifteen, and it leaves an obvious
 * home for the comment explaining why the answer is no longer the browser's to give.
 *
 * `requestIdentity` is kept for the same reason and now routes to sign-in. Its callers are
 * paths that cannot proceed without an identity (drawing, saving, claiming an image); the
 * right response to 'no identity' used to be a dialog and is now a sign-in page.
 */

import { router } from '../router.svelte';
import { auth } from './auth.svelte';

/**
 * Why an identity was needed. Retained so a caller can still say what it was doing, and so
 * the shape of these call sites did not have to change with the mechanism behind them.
 */
export type IdentityPromptReason = 'startup' | 'draw' | 'open-image' | 'save' | 'edit';

/**
 * Normalize a string into the shape an annotator id may take.
 *
 * No longer used to *create* one — the server derives ids from email addresses now
 * (`db/models.derive_annotator_id`) and pins the same `^[a-z0-9._-]{1,64}$` shape. Kept and
 * exported because it is the frontend's statement of that shape, and the admin screens
 * display ids that must round-trip through it unchanged.
 */
export function normalizeId(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

class AnnotatorIdentity {
  /** The provenance actor. Server-assigned; empty when signed out. */
  get id(): string {
    return auth.annotatorId;
  }

  get name(): string {
    return auth.displayName;
  }

  /**
   * Can this session write a box under a name?
   *
   * Both halves matter. A `viewer` is signed in and has an annotator id and still cannot
   * write, so a UI that only checked 'is there an id?' would offer them a draw tool that
   * every save would 403. An account with no linked annotator is the other half: the service
   * refuses its writes with a 409 rather than inventing an id, so the UI must not offer to
   * draw either.
   */
  get ready(): boolean {
    return auth.signedIn && auth.canWrite && auth.annotatorId.length > 0;
  }

  /** Signed in, but read-only. Worth distinguishing: the fix is not 'sign in'. */
  get readOnlyAccount(): boolean {
    return auth.signedIn && !auth.canWrite;
  }

  /**
   * Send the annotator somewhere they can fix the problem.
   *
   * A `viewer` is *not* sent to sign-in: they are already signed in, and bouncing them to a
   * login form they would complete successfully and land back in the same state is a loop.
   * Their situation needs an admin, and the components say so.
   */
  requestIdentity(reason: IdentityPromptReason): void {
    if (this.ready) return;
    if (this.readOnlyAccount) return;
    if (reason === 'startup' && auth.checking) return;
    router.go({ name: 'login' });
  }
}

export const annotator = new AnnotatorIdentity();
